import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";
import { getPracticeId } from "../config/practice";

/*
 * Billing module calls (v2 only, api/billing837/routes.py). Every call is pinned to v2 like the fax and
 * forms code (`_gw: "v2-pinned"`): the routes exist on v2 only, so they skip the v1/v2 routing rules,
 * never fall back to v1 and never trip the v2 circuit breaker. The server checks the "Billing Module"
 * privilege on every route (403 BILLING_PRIVILEGE).
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
const base = () => `/v1/${getPracticeId()}/billing-module`;
const enc = (v) => encodeURIComponent(String(v));

// The server's message for a failed call (its body is {status, code, message, detail}).
export const billingError = (e, fallback = "Something went wrong") =>
  e?.response?.data?.message || e?.message || fallback;
export const billingErrorBody = (e) => e?.response?.data || null;

export const getAccess = () => api.get(`${base()}/access`, V2);
export const getConfig = () => api.get(`${base()}/config`, V2);
export const putConfig = (config, version) => api.put(`${base()}/config`, { config, version }, V2);
export const validateConfig = (config) => api.post(`${base()}/config/validate`, { config }, V2);
export const getConfigAudit = () => api.get(`${base()}/config/audit`, V2);

export const listVisits = ({ from, to, providerid, departmentid }) => {
  const q = [`from=${enc(from)}`, `to=${enc(to)}`];
  if (providerid) q.push(`providerid=${enc(providerid)}`);
  if (departmentid) q.push(`departmentid=${enc(departmentid)}`);
  return api.get(`${base()}/visits?${q.join("&")}`, V2);
};

export const getClaim = (appointmentid) => api.get(`${base()}/claims/${enc(appointmentid)}`, V2);
export const buildClaim = (appointmentid, mode) =>
  api.post(`${base()}/claims/${enc(appointmentid)}/build`, { mode }, V2);
export const saveClaim = (appointmentid, claim, version) =>
  api.put(`${base()}/claims/${enc(appointmentid)}`, { claim, version: version ?? null }, V2);
export const setClaimStatus = (appointmentid, status, version) =>
  api.post(`${base()}/claims/${enc(appointmentid)}/status`, { status, version: version ?? null }, V2);
export const validateClaim = (appointmentid, claim) =>
  api.post(`${base()}/claims/${enc(appointmentid)}/validate`, { claim }, V2);

export const previewFile = (appointmentids) => api.post(`${base()}/generate/preview`, { appointmentids }, V2);
export const createFile = (appointmentids) => api.post(`${base()}/generate/file`, { appointmentids }, V2);
export const listBatches = () => api.get(`${base()}/batches`, V2);
export const athenaPreview = (appointmentids) => api.post(`${base()}/athena/preview`, { appointmentids }, V2);
export const athenaSubmit = (appointmentids, confirm_token) =>
  api.post(`${base()}/athena/submit`, { appointmentids, confirm_token }, V2);

// Download a batch file: the staff token goes with the request (api.js), the file arrives as a Blob and is
// saved through a temporary object URL (never a plain link: the file holds PHI and needs the login).
export async function downloadBatch(batchId, fileName) {
  const blob = await api.get(`${base()}/batches/${enc(batchId)}/download`, { ...V2, responseType: "blob" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || `batch-${batchId}.837`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
